import { useEffect, useState } from 'react'
import { AlertCircle, ArrowLeft, Loader2, UserRound } from 'lucide-react'

import { PinPad } from '@/componentes/ui/PinPad'
import { cargarUsuariosDeLaCaja, useSesion, type UsuarioParaPin } from '@/lib/sesion'
import { api, ErrorApi } from '@/lib/api'

// Entrada de la caja: el cajero toca su nombre y pone el PIN.
//
// Son dos pasos y no uno, y el motivo es el registro de autoría. La primera
// versión buscaba al dueño del PIN entre todos los del local; con cuatro
// dígitos y ocho empleados, la chance de que dos compartan número es de casi
// el 3%, y en ese caso la venta se le atribuía al que apareciera primero. En
// un sistema cuyo punto es poder mirar quién tocó el cajón, eso no sirve.
//
// De paso es más rápido: tocar la propia cara en una lista de cuatro es un
// gesto, y el sistema ya sabe contra qué hash verificar.
export function EntradaCaja() {
  const { entrarEnLaCaja } = useSesion()

  const [usuarios, setUsuarios] = useState<UsuarioParaPin[] | null>(null)
  const [elegido, setElegido] = useState<UsuarioParaPin | null>(null)
  const [pin, setPin] = useState('')
  const [enviando, setEnviando] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Si la caja ya bajó algo de la nube alguna vez. Null mientras no se sabe.
  //
  // Hace falta para no confundir dos cosas que se ven iguales y se arreglan al
  // revés: una caja recién registrada TODAVÍA no tiene la lista de empleados
  // —la sirve la nube y baja en la primera sincronización, unos segundos
  // después— y un local que de verdad no le cargó el PIN a nadie. El cartel
  // que había mandaba a Gestión a cargar un PIN que ya estaba cargado, que es
  // el peor consejo posible en el momento de estrenar una caja.
  const [sincronizoAlgunaVez, setSincronizoAlgunaVez] = useState<boolean | null>(null)

  useEffect(() => {
    let vigente = true
    let reloj: ReturnType<typeof setTimeout> | undefined

    const cargar = () => {
      cargarUsuariosDeLaCaja()
        .then(async (lista) => {
          if (!vigente) return

          setUsuarios(lista)

          // Con un solo empleado habilitado no hay nada que elegir: se salta
          // el paso. Es el caso de la verdulería chica, que es la mayoría.
          if (lista.length === 1) setElegido(lista[0]!)

          if (lista.length > 0) return

          // Con la lista vacía se pregunta POR QUÉ, y se vuelve a mirar cada
          // pocos segundos: la lista la trae la sincronización, así que la
          // pantalla se destraba sola en cuanto llega. Sin el reintento había
          // que reiniciar el programa para ver algo distinto, y reiniciar la
          // caja del mostrador no puede ser un paso de la instalación.
          const estado = await api
            .get<{ ultimaBajadaAt: string | null }>('/estado')
            .catch(() => null)

          if (!vigente) return

          setSincronizoAlgunaVez(estado === null ? null : estado.ultimaBajadaAt !== null)
          reloj = setTimeout(cargar, 4000)
        })
        // El catch no es formalidad: sin él, si el host local no responde la
        // pantalla se queda con la lista vacía y sin decir nada, y el cajero no
        // tiene forma de saber si el problema es la caja o su PIN.
        .catch(() => {
          if (vigente) setError('No pudimos leer los usuarios de esta caja.')
        })
    }

    cargar()

    return () => {
      vigente = false
      if (reloj !== undefined) clearTimeout(reloj)
    }
  }, [])

  const entrar = async (valor: string) => {
    if (!elegido || enviando) return

    setEnviando(true)
    setError(null)

    try {
      await entrarEnLaCaja(elegido.id, valor)
    } catch (e) {
      setError(e instanceof ErrorApi ? e.message : 'No se pudo entrar. Reintentá.')
      setPin('')
    } finally {
      setEnviando(false)
    }
  }

  if (usuarios === null) {
    return (
      <div className="flex min-h-screen items-center justify-center gap-2.5 bg-tinta text-crema">
        <Loader2 className="h-5 w-5 animate-spin" />
        <span className="text-[15px]">Preparando la caja…</span>
      </div>
    )
  }

  if (usuarios.length === 0) {
    // Recién registrada: la lista de empleados baja en la primera
    // sincronización y todavía no llegó. No hay nada que hacer más que
    // esperar unos segundos, y decirlo es la mitad del valor de esta
    // pantalla: el cartel de antes mandaba a Gestión a cargar un PIN que en
    // la mayoría de los casos ya estaba cargado.
    if (sincronizoAlgunaVez === false) {
      return (
        <Centrado>
          <Loader2 className="mx-auto mb-4 h-6 w-6 animate-spin text-crema/50" />

          <h1 className="text-[22px] font-bold tracking-[-0.02em]">Bajando los empleados</h1>

          <p className="mt-2 text-[14px] leading-relaxed text-crema/60">
            Esta caja se acaba de conectar y todavía no bajó la lista de quiénes pueden entrar. Es
            cuestión de unos segundos y se hace sola: la pantalla sigue sola cuando llegue.
          </p>

          <p className="mt-3 text-[13px] leading-relaxed text-crema/40">
            Si tarda más de un minuto, revisá la conexión del local.
          </p>
        </Centrado>
      )
    }

    return (
      <Centrado>
        <h1 className="text-[22px] font-bold tracking-[-0.02em]">Todavía no hay nadie habilitado</h1>

        <p className="mt-2 text-[14px] leading-relaxed text-crema/60">
          Para entrar a la caja hace falta un usuario con PIN. Se le carga desde Gestión, en la
          pantalla de usuarios, y baja a la caja en la próxima sincronización.
        </p>
      </Centrado>
    )
  }

  return (
    <Centrado>
      {!elegido ? (
        <>
          <h1 className="text-[24px] font-bold tracking-[-0.02em]">¿Quién atiende?</h1>

          <p className="mt-1.5 text-[14px] text-crema/55">Tocá tu nombre para poner el PIN.</p>

          <div className="mt-7 grid gap-2.5 sm:grid-cols-2">
            {usuarios.map((u) => (
              <button
                key={u.id}
                type="button"
                onClick={() => setElegido(u)}
                // 72 px de alto: se toca sin apuntar, que es como se opera un
                // mostrador con las manos ocupadas.
                className="flex min-h-[72px] items-center gap-3 rounded-xl border border-crema/12
                           bg-crema/[0.04] px-4 text-left transition
                           hover:border-verde-claro/40 hover:bg-crema/[0.08] active:scale-[0.98]"
              >
                <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-verde text-[15px] font-bold text-white">
                  {iniciales(u.nombre)}
                </span>

                <span className="min-w-0">
                  <span className="block truncate text-[15px] font-semibold">{u.nombre}</span>
                  <span className="block truncate text-[12.5px] text-crema/50">{u.rol}</span>
                </span>
              </button>
            ))}
          </div>
        </>
      ) : (
        <>
          {usuarios.length > 1 && (
            <button
              type="button"
              onClick={() => {
                setElegido(null)
                setPin('')
                setError(null)
              }}
              className="mb-5 flex items-center gap-1.5 text-[13.5px] text-crema/55 hover:text-crema"
            >
              <ArrowLeft className="h-4 w-4" /> Cambiar de usuario
            </button>
          )}

          <div className="flex items-center gap-3">
            <span className="grid h-12 w-12 place-items-center rounded-full bg-verde text-[16px] font-bold text-white">
              {iniciales(elegido.nombre)}
            </span>

            <div className="min-w-0">
              <h1 className="truncate text-[20px] font-bold tracking-[-0.02em]">
                {elegido.nombre}
              </h1>

              <p className="text-[13px] text-crema/50">{elegido.rol}</p>
            </div>
          </div>

          <p className="mt-5 text-[14px] text-crema/60">Poné tu PIN para abrir la caja.</p>

          {error && (
            <p className="mt-4 flex items-start gap-2 rounded-lg bg-rojo/15 px-3 py-2.5 text-[13.5px] text-[#F0A9A9]">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              {error}
            </p>
          )}

          <div className="mt-5">
            <PinPad
              valor={pin}
              onCambio={setPin}
              onConfirmar={entrar}
              deshabilitado={enviando}
              oscuro
            />
          </div>
        </>
      )}

      <p className="mt-8 flex items-center justify-center gap-1.5 text-[12px] text-crema/35">
        <UserRound className="h-3.5 w-3.5" />
        Cada venta queda registrada con el nombre de quien la cobró.
      </p>
    </Centrado>
  )
}

function Centrado({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-tinta px-5 py-10 text-crema">
      <div className="w-full max-w-[440px] animate-entrar">{children}</div>
    </div>
  )
}

function iniciales(nombre: string): string {
  const partes = nombre.split(' ').filter(Boolean)

  if (partes.length === 0) return '?'
  if (partes.length === 1) return partes[0]!.slice(0, 2).toUpperCase()

  return `${partes[0]![0]}${partes[partes.length - 1]![0]}`.toUpperCase()
}
